/**
 * InteractiveEffects Module
 *
 * Adds micro-interactions on hover to make the site feel more dynamic:
 * 3D card tilt, card lift, magnetic buttons, text shimmer and
 * lightweight image parallax.
 *
 * Every effect is skipped on touch-only devices and when the user
 * prefers reduced motion.
 *
 * @module InteractiveEffects
 */
export default class InteractiveEffects {

  static DEFAULTS = {
    selectors: {
      tilt: '.wleb-tilt, .wp-block-columns .has-surface-background-color',
      lift: '.wleb-lift, .wp-block-group[class*="has-surface"]',
      magneticButton: '.wp-block-button__link',
      shimmer: '.wleb-shimmer',
      parallax: '.wleb-parallax',
    },
    tilt: {
      maxDeg: 5,
      perspective: 800,
      transitionOut: 'transform 600ms cubic-bezier(0.23, 1, 0.32, 1)',
    },
    lift: {
      translateY: -8,
      transition: 'transform 500ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1)',
    },
    magnetic: {
      radius: 40,
      maxShift: 8,
      transitionOut: 'transform 500ms ease-out',
    },
    parallax: {
      factor: 0.1,
    },
  };

  /**
   * @param {Object} options - Merged with DEFAULTS.
   */
  constructor(options = {}) {
    this.config = { ...InteractiveEffects.DEFAULTS, ...options };

    // Skip entirely on touch-only devices or when reduced motion is preferred.
    this._isTouchDevice = !window.matchMedia('(hover: hover)').matches;
    this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this._isTouchDevice || this._prefersReducedMotion) return;

    // Bound handlers stored for later removal in destroy().
    this._boundHandlers = [];

    // Parallax state
    this._parallaxObserver = null;
    this._activeParallaxElements = new Set();
    this._parallaxTicking = false;
    this._onParallaxScrollBound = this._onParallaxScroll.bind(this);

    this._init();
  }

  /* ------------------------------------------------------------------
   * Bootstrap
   * ----------------------------------------------------------------*/

  _init() {
    this._initCardTilt();
    this._initCardLift();
    this._initMagneticButtons();
    this._initTextShimmer();
    this._initParallax();
  }

  /* ------------------------------------------------------------------
   * Utility — safe event binding with cleanup tracking
   * ----------------------------------------------------------------*/

  _bind(el, event, handler, options = {}) {
    el.addEventListener(event, handler, options);
    this._boundHandlers.push({ el, event, handler, options });
  }

  /* ------------------------------------------------------------------
   * 1. Card Tilt (3D perspective tilt on hover)
   * ----------------------------------------------------------------*/

  _initCardTilt() {
    const cards = document.querySelectorAll(this.config.selectors.tilt);
    if (!cards.length) return;

    cards.forEach((card) => {
      // Apply perspective on the parent so the 3D transform is visible.
      if (card.parentElement) {
        card.parentElement.style.perspective = `${this.config.tilt.perspective}px`;
      }

      card.style.willChange = 'transform';
      card.style.transition = this.config.tilt.transitionOut;

      const onMouseMove = (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Normalise to [-1, 1]
        const nx = (x / rect.width) * 2 - 1;
        const ny = (y / rect.height) * 2 - 1;

        const maxDeg = this.config.tilt.maxDeg;
        // rotateX is inverted so the card tilts towards the cursor
        const rotateX = (-ny * maxDeg).toFixed(2);
        const rotateY = (nx * maxDeg).toFixed(2);

        card.style.transition = 'none';
        card.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      };

      const onMouseLeave = () => {
        card.style.transition = this.config.tilt.transitionOut;
        card.style.transform = 'rotateX(0deg) rotateY(0deg)';
      };

      this._bind(card, 'mousemove', onMouseMove, { passive: true });
      this._bind(card, 'mouseleave', onMouseLeave);
    });
  }

  /* ------------------------------------------------------------------
   * 2. Card Lift (elevation on hover)
   * ----------------------------------------------------------------*/

  _initCardLift() {
    const cards = document.querySelectorAll(this.config.selectors.lift);
    if (!cards.length) return;

    cards.forEach((card) => {
      // Skip cards that already have tilt to avoid conflicting transforms.
      if (card.matches(this.config.selectors.tilt)) return;

      card.style.willChange = 'transform, box-shadow';
      card.style.transition = this.config.lift.transition;

      const onMouseEnter = () => {
        card.style.transform = `translateY(${this.config.lift.translateY}px)`;
        card.style.boxShadow = 'var(--wleb-shadow-card-hover)';
      };

      const onMouseLeave = () => {
        card.style.transform = 'translateY(0)';
        card.style.boxShadow = '';
      };

      this._bind(card, 'mouseenter', onMouseEnter);
      this._bind(card, 'mouseleave', onMouseLeave);
    });
  }

  /* ------------------------------------------------------------------
   * 3. Magnetic Buttons
   * ----------------------------------------------------------------*/

  _initMagneticButtons() {
    const buttons = document.querySelectorAll(this.config.selectors.magneticButton);
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      const onMouseMove = (e) => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);

        const radius = this.config.magnetic.radius + Math.max(rect.width, rect.height) / 2;

        if (dist < radius) {
          const maxShift = this.config.magnetic.maxShift;
          const strength = 1 - dist / radius;
          const tx = (dx * strength * maxShift / radius * 2).toFixed(2);
          const ty = (dy * strength * maxShift / radius * 2).toFixed(2);

          btn.style.transition = 'transform 150ms ease-out';
          btn.style.transform = `translate(${tx}px, ${ty}px)`;
        } else {
          btn.style.transition = this.config.magnetic.transitionOut;
          btn.style.transform = 'translate(0, 0)';
        }
      };

      const onMouseLeave = () => {
        btn.style.transition = this.config.magnetic.transitionOut;
        btn.style.transform = 'translate(0, 0)';
      };

      // Listen on the parent wrapper so the magnetic field extends beyond
      // the button itself.
      const wrapper = btn.closest('.wp-block-button') || btn.parentElement;
      if (wrapper) {
        this._bind(wrapper, 'mousemove', onMouseMove, { passive: true });
        this._bind(wrapper, 'mouseleave', onMouseLeave);
      }
    });
  }

  /* ------------------------------------------------------------------
   * 4. Text Shimmer
   * ----------------------------------------------------------------*/

  _initTextShimmer() {
    const shimmers = document.querySelectorAll(this.config.selectors.shimmer);
    if (!shimmers.length) return;

    shimmers.forEach((el) => {
      // Find the closest section/group parent so the trigger area is generous.
      const trigger = el.closest('section, .wp-block-group, .wp-block-cover, .wp-block-columns') || el.parentElement;

      const onMouseEnter = () => {
        // Remove class first to allow re-triggering.
        el.classList.remove('is-shimmering');
        // Force a reflow so the animation restarts.
        void el.offsetWidth;
        el.classList.add('is-shimmering');
      };

      const onAnimationEnd = () => {
        el.classList.remove('is-shimmering');
      };

      if (trigger) {
        this._bind(trigger, 'mouseenter', onMouseEnter);
      }
      this._bind(el, 'animationend', onAnimationEnd);
    });
  }

  /* ------------------------------------------------------------------
   * 5. Parallax (lightweight scroll-based image offset)
   * ----------------------------------------------------------------*/

  _initParallax() {
    const elements = document.querySelectorAll(this.config.selectors.parallax);
    if (!elements.length) return;

    this._parallaxObserver = new IntersectionObserver(
      this._handleParallaxVisibility.bind(this),
      { threshold: 0 },
    );

    elements.forEach((el) => {
      el.style.willChange = 'transform';
      this._parallaxObserver.observe(el);
    });

    window.addEventListener('scroll', this._onParallaxScrollBound, { passive: true });
  }

  _handleParallaxVisibility(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._activeParallaxElements.add(entry.target);
      } else {
        this._activeParallaxElements.delete(entry.target);
      }
    });
  }

  _onParallaxScroll() {
    if (this._parallaxTicking || !this._activeParallaxElements.size) return;

    this._parallaxTicking = true;

    window.requestAnimationFrame(() => {
      this._activeParallaxElements.forEach((el) => {
        const factor = parseFloat(el.getAttribute('data-parallax-factor')) || this.config.parallax.factor;
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const centre = rect.top + rect.height / 2;
        const offset = (centre - windowHeight / 2) * factor;

        el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
      });

      this._parallaxTicking = false;
    });
  }

  /* ------------------------------------------------------------------
   * Public API
   * ----------------------------------------------------------------*/

  /** Tear down all listeners and observers. */
  destroy() {
    // Remove every event listener registered via _bind().
    if (this._boundHandlers) {
      this._boundHandlers.forEach(({ el, event, handler, options }) => {
        el.removeEventListener(event, handler, options);
      });
      this._boundHandlers = [];
    }

    // Parallax cleanup
    if (this._parallaxObserver) {
      this._parallaxObserver.disconnect();
      this._parallaxObserver = null;
    }
    window.removeEventListener('scroll', this._onParallaxScrollBound, { passive: true });
    this._activeParallaxElements?.clear();
  }
}
